// Thin client for the server's account endpoints.

export interface AuthStatus {
  userId: string | null;
  setupRequired: boolean;
  signupOpen: boolean;
  /** present only on local development servers with seeded demo accounts */
  demo: { password: string } | null;
}

export interface InviteInfo {
  workspaceName: string;
  workspaceColor?: string;
  inviterName: string;
  email: string;
  hasAccount: boolean;
  role?: 'member' | 'admin' | 'guest';
}

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? undefined : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw Object.assign(new Error(data.error ?? `Request failed (${res.status})`), { status: res.status });
  return data;
}

export const authApi = {
  status: () => call<AuthStatus>('GET', '/api/auth/status'),
  login: (email: string, password: string) => call<{ userId: string }>('POST', '/api/auth/login', { email, password }),
  setup: (input: { name: string; email: string; password: string; workspaceName: string }) => call<{ userId: string; workspaceId: string }>('POST', '/api/auth/setup', input),
  changePassword: (current: string, next: string) => call<{ ok: true }>('POST', '/api/auth/password', { current, next }),
  changeEmail: (email: string, password: string) => call<{ ok: true }>('POST', '/api/auth/email', { email, password }),
  createInvite: (workspaceId: string, email: string, role: 'member' | 'admin' | 'guest' = 'member') =>
    call<{ url: string; existingAccount: boolean }>('POST', '/api/invites', { workspaceId, email, role }),
  getInvite: (token: string) => call<InviteInfo>('GET', `/api/invites/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, input: { name?: string; password?: string }) => call<{ userId: string; workspaceId: string }>('POST', `/api/invites/${encodeURIComponent(token)}/accept`, input),
  createResetLink: (userId: string) => call<{ url: string }>('POST', '/api/auth/reset-links', { userId }),
  getReset: (token: string) => call<{ email: string }>('GET', `/api/resets/${encodeURIComponent(token)}`),
  resetPassword: (token: string, password: string) => call<{ userId: string }>('POST', `/api/resets/${encodeURIComponent(token)}`, { password }),
};

export function errorMessage(e: unknown) {
  return e instanceof Error ? e.message : 'Something went wrong.';
}
